/**
 * Differentiation Section Component
 * Highlights what makes OKR Hub different with a comparison layout
 */

import React, { useState, useEffect, useRef } from 'react';
import { Section } from './Section';
import { X, Check, ArrowRight } from 'lucide-react';

interface Comparison {
  not: string;
  but: string;
}

const comparisons: Comparison[] = [
  {
    not: 'Tool-led implementation',
    but: 'Consultant-led, context-first approach'
  },
  {
    not: 'Built by theorists',
    but: 'Built by practitioners with real experience'
  },
  {
    not: 'Mechanical metric tracking',
    but: 'Human-centric decision support'
  },
  {
    not: 'Designed for reporting',
    but: 'Designed for execution and results'
  },
  {
    not: 'One-size-fits-all',
    but: 'Adapted for Indian and global contexts'
  }
];

export const DifferentiationSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Section id="differentiation" background="sand" padding="xl">
      <div ref={sectionRef} className="max-w-5xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="font-display text-4xl md:text-5xl text-ink mb-4">
            What Makes OKR Hub Different
          </h2>
          <p className="text-xl text-ink-muted max-w-2xl mx-auto">
            We took a different path. Here's how we compare.
          </p>
        </div>

        {/* Comparison Table */}
        <div className={`bg-white rounded-3xl shadow-xl overflow-hidden mb-12 transition-all duration-700 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Table Header */}
          <div className="grid grid-cols-2">
            <div className="bg-stone-100 px-6 py-4 md:px-8 md:py-5 border-b-2 border-stone-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-stone-300 rounded-full flex items-center justify-center">
                  <X className="w-4 h-4 text-stone-600" />
                </div>
                <span className="font-semibold text-ink-muted text-sm md:text-base uppercase tracking-wide">Not This</span>
              </div>
            </div>
            <div className="bg-primary-500 px-6 py-4 md:px-8 md:py-5 border-b-2 border-primary-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-white text-sm md:text-base uppercase tracking-wide">But This</span>
              </div>
            </div>
          </div>

          {/* Table Rows */}
          {comparisons.map((item, index) => (
            <div
              key={index}
              className={`grid grid-cols-2 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              {/* Not This */}
              <div className={`px-6 py-5 md:px-8 md:py-6 flex items-center gap-3 ${
                index !== comparisons.length - 1 ? 'border-b border-stone-200' : ''
              } bg-stone-50 hover:bg-stone-100 transition-colors`}>
                <X className="w-5 h-5 text-stone-400 flex-shrink-0" />
                <span className="text-ink-muted text-sm md:text-base line-through decoration-stone-300">
                  {item.not}
                </span>
              </div>

              {/* But This */}
              <div className={`px-6 py-5 md:px-8 md:py-6 flex items-center gap-3 ${
                index !== comparisons.length - 1 ? 'border-b border-primary-100' : ''
              } bg-primary-50 hover:bg-primary-100 transition-colors`}>
                <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <span className="text-ink font-medium text-sm md:text-base">
                  {item.but}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Closing Statement */}
        <div className={`transition-all duration-700 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="relative bg-gradient-to-r from-ink via-stone-800 to-ink rounded-3xl p-8 md:p-12 text-white text-center overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-primary-400/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <p className="text-2xl md:text-4xl font-display leading-relaxed mb-2">
                We don't sell OKRs.
              </p>
              <p className="text-xl md:text-2xl text-stone-300 mb-8">
                We help you run your business better using OKRs.
              </p>
              <button
                onClick={() => scrollToSection('contact')}
                className="inline-flex items-center gap-2 bg-primary-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-600 hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Start the Conversation
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
